import * as React from 'react'
import {
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  X,
  Clock,
  Info,
  FileText,
  Sliders,
  Shield,
  Zap,
  RotateCcw,
  TrendingUp,
} from 'lucide-react'
import { usePersistStore, defaultSP80090BParams } from '@/stores/persistStore'
import { FileUploader } from '@/components/FileUploader'
import { ImportDataBanner } from '@/components/ImportDataBanner'
import type { ParseResult } from '@/lib/fileParser'
import { DataGenerator, type DataGeneratorMeta } from '@/components/DataGenerator'
import type { InputFormat } from '@/types'
import { ExportButton } from '@/components/ExportButton'
import { ResultSummaryBar } from '@/components/ResultSummaryBar'
import { SnapshotManager } from '@/components/SnapshotManager'
import { DataExportMenu } from '@/components/DataExportMenu'
import { SectionHeader } from '@/components/SectionHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Cell,
} from 'recharts'
import { ChartExportButton } from '@/components/ChartExportButton'
import { cn } from '@/lib/utils'
import {
  DEFAULT_SP80090B_PARAMS,
  MIN_SAMPLES_SP80090B,
  APP_VERSION,
} from '@/lib/constants'
import type {
  SP80090BResult,
  SP80090BParameters,
  IIDStatisticResult,
  EntropyEstimateResult,
  ExportReport,
} from '@/types'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(ms: number): string {
  if (ms < 1000) return `${ms.toFixed(0)}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  const mins = Math.floor(ms / 60_000)
  const secs = ((ms % 60_000) / 1000).toFixed(0)
  return `${mins}m ${secs}s`
}

function formatEntropy(v: number): string {
  if (!isFinite(v) || isNaN(v)) return '---'
  return v.toFixed(6)
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SP80090BPage() {
  const persist = usePersistStore()
  const {
    sp80090bParams: persistedParams,
    sp80090bResult: persistedResult,
    setSP80090BParams: persistParams,
    setSP80090BResult: persistResult,
    clearSP80090BResult,
  } = persist

  // Per-module input data (persisted)
  const [inputData, setInputDataLocal] = React.useState<ParseResult | null>(
    () => persist.getModuleInput('sp80090b')
  )
  const setInputData = React.useCallback((data: ParseResult) => {
    setInputDataLocal(data)
    persist.setModuleInput('sp80090b', data)
  }, [persist])

  // Parameters (initialize from persist store, fallback to defaults)
  const [symbolSize, setSymbolSize] = React.useState<number>(
    persistedParams?.symbolSize ?? DEFAULT_SP80090B_PARAMS.symbolSize,
  )
  const [numPermutations, setNumPermutations] = React.useState<number>(
    persistedParams?.numPermutations ?? DEFAULT_SP80090B_PARAMS.numPermutations,
  )
  const [verboseIID, setVerboseIID] = React.useState<boolean>(
    persistedParams?.verboseIID ?? DEFAULT_SP80090B_PARAMS.verboseIID,
  )

  // Execution state
  const [isRunning, setIsRunning] = React.useState(false)
  const [progressMsg, setProgressMsg] = React.useState<string | null>(null)
  const [progressPct, setProgressPct] = React.useState(0)
  const [localResult, setLocalResult] = React.useState<SP80090BResult | null>(null)
  const result: SP80090BResult | null = localResult ?? persistedResult ?? null
  const setResult = React.useCallback((r: SP80090BResult | null) => {
    setLocalResult(r)
    if (r) persistResult(r)
    else clearSP80090BResult()
  }, [persistResult, clearSP80090BResult])
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (persistedParams) {
      setSymbolSize(persistedParams.symbolSize)
      setNumPermutations(persistedParams.numPermutations)
      setVerboseIID(persistedParams.verboseIID)
    }
  }, [persistedParams])

  // Sync params to persist store
  React.useEffect(() => {
    persistParams({ symbolSize, numPermutations, verboseIID })
  }, [symbolSize, numPermutations, verboseIID, persistParams])

  // Restore defaults handler
  const restoreDefaults = React.useCallback(() => {
    setSymbolSize(defaultSP80090BParams.symbolSize)
    setNumPermutations(defaultSP80090BParams.numPermutations)
    setVerboseIID(defaultSP80090BParams.verboseIID)
  }, [])

  // Close results handler
  const handleCloseResults = React.useCallback(() => {
    setResult(null)
    clearSP80090BResult()
  }, [clearSP80090BResult])

  const workerRef = React.useRef<Worker | null>(null)

  // Handle generated data from DataGenerator
  const handleGeneratedData = React.useCallback(
    (data: Uint8Array | Float64Array, meta: DataGeneratorMeta) => {
      if (!(data instanceof Uint8Array) || !meta.bitLength) return
      setInputData({
        kind: 'binary',
        input: {
          data,
          bitLength: meta.bitLength,
          sampleCount: meta.bitLength,
          fileName: `generated-${meta.description.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`,
          fileSize: data.byteLength,
          format: 'binary' as InputFormat,
        },
      })
      setResult(null)
      setError(null)
    },
    [setInputData],
  )

  // Determine available sample data for the worker
  const sampleData = React.useMemo<Uint8Array | null>(() => {
    if (!inputData) return null

    if (inputData.kind === 'binary') {
      // For binary data, the packed bytes are the symbols when symbolSize=1
      return inputData.input.data
    }
    if (inputData.kind === 'symbol') {
      return inputData.input.data
    }
    // Continuous data is not directly usable for 90B
    return null
  }, [inputData])

  const sampleCount = React.useMemo(() => {
    if (!inputData) return 0
    if (inputData.kind === 'binary') return inputData.input.bitLength
    if (inputData.kind === 'symbol') return inputData.input.data.length
    return 0
  }, [inputData])

  const canRun =
    sampleData !== null && sampleCount >= MIN_SAMPLES_SP80090B && !isRunning

  // Cleanup worker on unmount
  React.useEffect(() => {
    return () => {
      workerRef.current?.terminate()
    }
  }, [])

  // ---------------------------------------------------------------------------
  // Run analysis
  // ---------------------------------------------------------------------------
  const handleRun = React.useCallback(() => {
    if (!sampleData) return

    // Instantly jump to top, THEN update state — avoids layout-shift jitter
    window.scrollTo(0, 0)

    setIsRunning(true)
    setResult(null)
    setError(null)
    setProgressMsg('Initialising worker...')
    setProgressPct(0)

    // Terminate any previous worker
    workerRef.current?.terminate()

    const worker = new Worker(
      new URL('@/engine/workers/sp80090bWorker.ts', import.meta.url),
      { type: 'module' },
    )
    workerRef.current = worker

    const params: Partial<SP80090BParameters> = {
      symbolSize,
      numPermutations,
      verboseIID,
    }

    worker.onmessage = (e: MessageEvent) => {
      const msg = e.data as {
        type: string
        message?: string
        progress?: number
        result?: SP80090BResult
        error?: string
      }

      if (msg.type === 'progress') {
        setProgressMsg(msg.message ?? null)
        setProgressPct(Math.round((msg.progress ?? 0) * 100))
      } else if (msg.type === 'complete') {
        const r = msg.result ?? null
        setResult(r)
        if (r) persistResult(r)
        setIsRunning(false)
        setProgressMsg(null)
        setProgressPct(100)
        worker.terminate()
      } else if (msg.type === 'error') {
        setError(msg.error ?? 'Unknown worker error')
        setIsRunning(false)
        setProgressMsg(null)
        worker.terminate()
      }
    }

    worker.onerror = (ev) => {
      setError(ev.message ?? 'Worker error')
      setIsRunning(false)
      worker.terminate()
    }

    // Transfer the ArrayBuffer for performance
    const buffer = sampleData.buffer.slice(
      sampleData.byteOffset,
      sampleData.byteOffset + sampleData.byteLength,
    )
    worker.postMessage(
      { type: 'run', data: buffer, symbolSize, params, bitLength: sampleCount },
      [buffer],
    )
  }, [sampleData, sampleCount, symbolSize, numPermutations, verboseIID, persistResult])

  // ---------------------------------------------------------------------------
  // Export report builder
  // ---------------------------------------------------------------------------
  const exportReport = React.useMemo<ExportReport | null>(() => {
    if (!result || !inputData) return null
    const info =
      inputData.kind === 'binary'
        ? inputData.input
        : inputData.kind === 'symbol'
          ? inputData.input
          : inputData.input
    return {
      version: APP_VERSION,
      timestamp: new Date().toISOString(),
      inputFile: {
        name: info.fileName,
        size: info.fileSize,
        sha256: info.sha256 ?? '',
        sampleCount: info.sampleCount,
        format: info.format,
      },
      results: {
        sp80090b: result,
      },
    }
  }, [result, inputData])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-hidden">
      {/* Header */}
      <h1 className="text-2xl font-bold mb-2">
        NIST SP 800-90B Entropy Estimation
      </h1>
      <p className="text-muted-foreground mb-8">
        IID hypothesis testing and min-entropy estimation for entropy sources.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
        {/* ================================================================ */}
        {/* LEFT PANEL — Input & Parameters                                  */}
        {/* ================================================================ */}
        <div className="space-y-6 min-w-0">
          <SectionHeader title="Input" icon={FileText} />

          {/* File upload */}
          <FileUploader
            acceptedKinds={['binary', 'symbol']}
            formatHints="binary"
            kindMismatchMessage="This module requires binary or symbol data. For continuous data, use the Gaussian page with PIT Bridge."
            moduleData={inputData}
            onModuleClear={() => { setInputDataLocal(null); persist.clearModuleInput('sp80090b') }}
            onDataLoaded={(r) => { setInputDataLocal(r); persist.setModuleInput('sp80090b', r); setLocalResult(null) }}
          />

          {/* Import from other modules */}
          {!inputData && (
            <ImportDataBanner
              currentModule="sp80090b"
              acceptedKinds={['binary', 'symbol']}
              onImport={setInputData}
            />
          )}

          {/* OR separator + Data Generator */}
          <div className="relative flex items-center justify-center">
            <Separator className="flex-1" />
            <span className="px-3 text-xs font-medium text-muted-foreground uppercase">
              or
            </span>
            <Separator className="flex-1" />
          </div>

          <DataGenerator mode="binary" onDataGenerated={handleGeneratedData} />

          <SectionHeader title="Parameters" icon={Sliders} />

          {/* Parameters */}
          <Card role="region" aria-label="Analysis parameters">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Parameters</CardTitle>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={restoreDefaults}>
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Defaults
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Symbol size */}
              <div className="space-y-2">
                <Label htmlFor="symbolSize" className="flex items-center gap-1.5">
                  Symbol size (bits)
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground/50 cursor-help">
                          <Info className="h-3 w-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-[240px]">
                        <p>Number of bits per symbol. Use 1 for binary data (e.g., MTJ p-bit output). Higher values (2–8) are for multi-level sources where each sample is a byte value 0–2ⁿ-1.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Select
                  value={String(symbolSize)}
                  onValueChange={(v) => setSymbolSize(Number(v))}
                >
                  <SelectTrigger id="symbolSize">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                      <SelectItem key={s} value={String(s)}>
                        {s} {s === 1 ? '(binary)' : `(${Math.pow(2, s)} symbols)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Permutations */}
              <div className="space-y-2">
                <Label htmlFor="numPerm" className="flex items-center gap-1.5">
                  Number of permutations
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground/50 cursor-help">
                          <Info className="h-3 w-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-[240px]">
                        <p>Number of Fisher-Yates shuffles for the IID hypothesis test. 1,000 is fast (~5s) but less precise; 10,000 is the SP 800-90B standard and gives more reliable results (~2–3 min for 1M samples).</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Select
                  value={String(numPermutations)}
                  onValueChange={(v) => setNumPermutations(Number(v))}
                >
                  <SelectTrigger id="numPerm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1000">1,000 (fast)</SelectItem>
                    <SelectItem value="10000">
                      10,000 (recommended)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Verbose IID toggle */}
              <div className="flex items-center justify-between">
                <Label htmlFor="verbose" className="cursor-pointer flex items-center gap-1.5">
                  Verbose IID results
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground/50 cursor-help">
                          <Info className="h-3 w-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-[240px]">
                        <p>When enabled, shows all 21 individual IID test statistics (including chi-square independence and goodness-of-fit). When disabled, only failed statistics are shown with passed statistics collapsed.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Switch
                  id="verbose"
                  checked={verboseIID}
                  onCheckedChange={setVerboseIID}
                />
              </div>

              <Separator />

              {/* Run button */}
              <Button
                className="w-full"
                size="lg"
                disabled={!canRun}
                onClick={handleRun}
                data-testid="run-analysis-btn"
              >
                {isRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Run Analysis
                  </>
                )}
              </Button>

            </CardContent>
          </Card>

          {/* Data requirements note */}
          <div className="flex gap-2 rounded-md bg-primary/5 p-3">
            <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              For binary MTJ data, use symbol size = 1. Minimum{' '}
              {MIN_SAMPLES_SP80090B.toLocaleString()} samples required.
            </p>
          </div>
        </div>

        {/* ================================================================ */}
        {/* RIGHT PANEL — Results                                            */}
        {/* ================================================================ */}
        <div className="space-y-6 min-w-0">
          <SectionHeader
            title="Results"
            icon={TrendingUp}
            right={
              result ? (
                <>
                  {exportReport && <ExportButton report={exportReport} />}
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCloseResults}>
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : undefined
            }
          />

          {/* Progress */}
          {isRunning && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Progress</CardTitle>
                  <span className="text-sm text-muted-foreground">
                    {progressPct}%
                  </span>
                </div>
                <Progress value={progressPct} className="mt-2" />
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {progressMsg ?? 'Working...'}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Error */}
          {error && (
            <Card className="border-destructive/50">
              <CardContent className="p-6">
                <p className="text-sm text-destructive">{error}</p>
              </CardContent>
            </Card>
          )}

          {/* No results placeholder */}
          {!result && !isRunning && !error && (
            <Card className="border-dashed">
              <CardContent className="p-12">
                <div className="flex flex-col items-center gap-4 text-center">
                  <div className="rounded-full bg-primary/10 p-4">
                    <Shield className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Ready for Analysis</h3>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Upload data and click &ldquo;Run Analysis&rdquo; to perform IID hypothesis testing and min-entropy estimation.
                      Results will appear as each stage completes.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2 w-full max-w-lg">
                    <div className="rounded-lg border bg-card/50 p-3 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <Shield className="h-3.5 w-3.5 text-blue-500" />
                        <p className="text-xs font-medium">IID Testing</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground">21 statistics via permutation test</p>
                    </div>
                    <div className="rounded-lg border bg-card/50 p-3 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <Zap className="h-3.5 w-3.5 text-green-500" />
                        <p className="text-xs font-medium">Entropy</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground">10 min-entropy estimators</p>
                    </div>
                    <div className="rounded-lg border bg-card/50 p-3 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <Info className="h-3.5 w-3.5 text-orange-500" />
                        <p className="text-xs font-medium">Result</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground">Conservative min-entropy bound</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ============================================================= */}
          {/* IID TEST RESULTS                                               */}
          {/* ============================================================= */}
          {result && (
            <>
              <ResultSummaryBar
                passCount={result.iidTestResults.statistics.filter((s) => s.passed).length}
                failCount={result.iidTestResults.statistics.filter((s) => !s.passed).length}
                totalTests={result.iidTestResults.statistics.length}
                overallPassed={result.iidTestResults.iidConclusion}
                executionTimeMs={result.executionTimeMs}
                passMessage={`IID hypothesis accepted \u00B7 H\u2098\u1D62\u2099 = ${result.finalMinEntropy.toFixed(4)} bits`}
                failMessage={`IID hypothesis rejected \u00B7 H\u2098\u1D62\u2099 = ${result.finalMinEntropy.toFixed(4)} bits`}
                detail={`${result.entropyEstimates.length} estimators \u00B7 ${result.iidTestResults.numPermutations.toLocaleString()} permutations`}
              />

              <SnapshotManager module="sp80090b" hasResult={!!result} onLoadSnapshot={(snap) => {
                setLocalResult(snap.result)
              }} />

              <IIDResultsSection
                statistics={result.iidTestResults.statistics}
                conclusion={result.iidTestResults.iidConclusion}
                numPermutations={result.iidTestResults.numPermutations}
                verbose={verboseIID}
              />

              <EntropyBarChart
                estimates={result.entropyEstimates}
                finalMinEntropy={result.finalMinEntropy}
                symbolSize={symbolSize}
              />

              <EntropyEstimatesSection
                estimates={result.entropyEstimates}
                finalMinEntropy={result.finalMinEntropy}
                theoreticalMax={result.theoreticalMaxEntropy}
                efficiency={result.entropyEfficiency}
              />

              {/* Detailed results + export row */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  Detailed estimator results and IID statistics above.
                </p>
                <div className="flex items-center gap-1 shrink-0">
                  {exportReport && <ExportButton report={exportReport} />}
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCloseResults}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Execution time */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    Total execution time:{' '}
                    <span className="font-mono font-medium text-foreground">
                      {formatTime(result.executionTimeMs)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ===========================================================================
// IID Results Sub-Section
// ===========================================================================

interface IIDResultsSectionProps {
  statistics: IIDStatisticResult[]
  conclusion: boolean
  numPermutations: number
  verbose: boolean
}

function IIDResultsSection({
  statistics,
  conclusion,
  numPermutations,
  verbose,
}: IIDResultsSectionProps) {
  const passCount = statistics.filter((s) => s.passed).length
  const failCount = statistics.length - passCount

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            IID Hypothesis Test
            <DataExportMenu
              data={statistics.map((s) => ({
                name: s.name,
                originalValue: s.originalValue,
                rank: s.rank,
                totalPermutations: s.totalPermutations,
                passed: s.passed,
              }))}
              fileName="sp800-90b-iid-statistics"
              columns={['name', 'originalValue', 'rank', 'totalPermutations', 'passed']}
              title="SP 800-90B IID Statistics"
            />
          </CardTitle>
          <Badge
            variant={conclusion ? 'success' : 'destructive'}
            className="text-sm px-3 py-1"
          >
            {conclusion ? 'IID' : 'Non-IID'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary row */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
          <div className="rounded-lg bg-muted/50 p-2 sm:p-3">
            <p className="text-xs text-muted-foreground">Tests Run</p>
            <p className="text-lg font-semibold font-mono">
              {statistics.length}
            </p>
          </div>
          <div className="rounded-lg bg-green-500/10 p-2 sm:p-3">
            <p className="text-xs text-muted-foreground">Passed</p>
            <p className="text-lg font-semibold font-mono text-green-600 dark:text-green-400">
              {passCount}
            </p>
          </div>
          <div className="rounded-lg bg-red-500/10 p-2 sm:p-3">
            <p className="text-xs text-muted-foreground">Failed</p>
            <p className="text-lg font-semibold font-mono text-red-600 dark:text-red-400">
              {failCount}
            </p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Permutation test with {numPermutations.toLocaleString()} permutations.
          A statistic fails if its rank is in the top or bottom 5 out of{' '}
          {numPermutations.toLocaleString()}.
        </p>

        <Separator />

        {/* Statistic list (always show all 19 if verbose, otherwise compact) */}
        {verbose ? (
          <div className="space-y-2 overflow-x-auto">
            {statistics.map((stat, idx) => (
              <IIDStatisticRow key={idx} stat={stat} />
            ))}
          </div>
        ) : (
          <div className="space-y-2 overflow-x-auto">
            {/* Show failed first, then passed in compact mode */}
            {statistics
              .filter((s) => !s.passed)
              .map((stat, idx) => (
                <IIDStatisticRow key={`fail-${idx}`} stat={stat} />
              ))}
            {statistics.filter((s) => s.passed).length > 0 && (
              <CollapsiblePassedStats
                stats={statistics.filter((s) => s.passed)}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function IIDStatisticRow({ stat }: { stat: IIDStatisticResult }) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-2 sm:gap-3 rounded-md px-2 sm:px-3 py-2 text-sm',
        !stat.passed && 'bg-red-500/5',
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        {stat.passed ? (
          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
        ) : (
          <XCircle className="h-4 w-4 text-red-500 shrink-0" />
        )}
        <span className="truncate">{stat.name}</span>
      </div>
      <div className="flex items-center gap-2 sm:gap-4 shrink-0 text-xs text-muted-foreground">
        <span className="font-mono text-[11px] sm:text-xs">T = {stat.originalValue.toFixed(4)}</span>
        <span className="font-mono text-[11px] sm:text-xs hidden sm:inline">
          rank {stat.rank}/{stat.totalPermutations}
        </span>
        <Badge variant={stat.passed ? 'success' : 'destructive'} className="text-[10px]">
          {stat.passed ? 'Pass' : 'Fail'}
        </Badge>
      </div>
    </div>
  )
}

function CollapsiblePassedStats({ stats }: { stats: IIDStatisticResult[] }) {
  const [expanded, setExpanded] = React.useState(false)

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
      >
        <CheckCircle2 className="h-4 w-4 text-green-500" />
        {expanded
          ? `Hide ${stats.length} passed statistics`
          : `Show ${stats.length} passed statistics`}
      </button>
      {expanded &&
        stats.map((stat, idx) => (
          <IIDStatisticRow key={`pass-${idx}`} stat={stat} />
        ))}
    </div>
  )
}

// ===========================================================================
// Entropy Bar Chart
// ===========================================================================

/** Short labels for entropy estimators */
const ESTIMATOR_SHORT_NAMES: Record<string, string> = {
  'Most Common Value': 'MCV',
  'Collision': 'Collision',
  'Markov': 'Markov',
  'Compression': 'Compress',
  'T-Tuple': 'T-Tuple',
  'LRS': 'LRS',
  'MultiMCW': 'MultiMCW',
  'Lag': 'Lag',
  'MultiMMC': 'MultiMMC',
  'LZ78Y': 'LZ78Y',
}

interface EntropyBarChartProps {
  estimates: EntropyEstimateResult[]
  finalMinEntropy: number
  symbolSize: number
}

function EntropyBarChart({ estimates, finalMinEntropy, symbolSize }: EntropyBarChartProps) {
  const chartRef = React.useRef<HTMLDivElement>(null)
  const isDark = document.documentElement.classList.contains('dark')
  const cl = {
    muted: isDark ? '#a1a1aa' : '#71717a',
    border: isDark ? '#3f3f46' : '#e4e4e7',
    accent: isDark ? '#27272a' : '#f4f4f5',
  }

  const plotData = estimates.map((est, i) => ({
    x: i,
    name: ESTIMATOR_SHORT_NAMES[est.estimatorName] ?? est.estimatorName.substring(0, 6),
    fullName: est.estimatorName,
    minEntropy: est.minEntropy,
    isMin: Math.abs(est.minEntropy - finalMinEntropy) < 1e-9,
  }))

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            Entropy Estimates
            <span className="text-[10px] font-normal text-muted-foreground ml-1">(bits per symbol)</span>
          </CardTitle>
          <ChartExportButton containerRef={chartRef} fileName="sp800-90b-entropy" />
        </div>
      </CardHeader>
      <CardContent className="pb-4 px-2 sm:px-4">
        <div ref={chartRef} role="img" aria-label="Entropy estimates chart showing min-entropy values from each estimator. Lower values indicate less randomness.">
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 12, right: 12, bottom: 50, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cl.border} opacity={0.5} horizontal vertical={false} />
                <XAxis
                  dataKey="x" type="number"
                  domain={[-0.5, plotData.length - 0.5]}
                  ticks={plotData.map((_, i) => i)}
                  tickFormatter={(i: number) => plotData[i]?.name ?? ''}
                  tick={{ fontSize: 8, fill: cl.muted }}
                  tickLine={false}
                  axisLine={{ stroke: cl.border }}
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  dataKey="minEntropy" type="number"
                  domain={[0, symbolSize]}
                  tick={{ fontSize: 9, fill: cl.muted }}
                  tickLine={false} axisLine={false} width={36}
                />
                <ReferenceLine
                  y={finalMinEntropy}
                  stroke="#ef4444" strokeDasharray="6 3" strokeWidth={2}
                  label={{
                    value: `min = ${finalMinEntropy.toFixed(3)}`,
                    position: 'insideBottomLeft',
                    style: { fontSize: 9, fill: '#ef4444', fontWeight: 600 },
                    offset: 4,
                  }}
                />
                <RechartsTooltip content={({ active, payload }: any) => {
                  if (!active || !payload?.[0]) return null
                  const d = payload[0].payload
                  return (
                    <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
                      <p className="font-medium">{d.fullName}</p>
                      <p className="font-mono mt-0.5">H_min = {d.minEntropy.toFixed(6)}</p>
                      {d.isMin && <p className="text-primary font-medium mt-0.5">Final estimate (minimum)</p>}
                    </div>
                  )
                }} />
                <Scatter data={plotData} isAnimationActive={false}>
                  {plotData.map((entry, i) => (
                    <Cell key={i} fill={entry.isMin ? '#ef4444' : '#22c55e'} r={6} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1 px-2">
          Each dot is one estimator's min-entropy. The red dashed line marks the final (minimum) estimate.
          Red dot = the determining estimator.
        </p>
      </CardContent>
    </Card>
  )
}

// ===========================================================================
// Entropy Estimates Sub-Section
// ===========================================================================

interface EntropyEstimatesSectionProps {
  estimates: EntropyEstimateResult[]
  finalMinEntropy: number
  theoreticalMax: number
  efficiency: number
}

function EntropyEstimatesSection({
  estimates,
  finalMinEntropy,
  theoreticalMax,
  efficiency,
}: EntropyEstimatesSectionProps) {
  const efficiencyPct = Math.round(efficiency * 100)

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Entropy Estimation
            <DataExportMenu
              data={estimates.map((e) => ({
                estimator: e.estimatorName,
                minEntropy: e.minEntropy,
              }))}
              fileName="sp800-90b-entropy-estimates"
              columns={['estimator', 'minEntropy']}
              title="SP 800-90B Entropy Estimates"
            />
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Final min-entropy highlight */}
        <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-3 sm:p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">
            Final Min-Entropy Estimate
          </p>
          <p className="text-2xl sm:text-3xl font-bold font-mono text-primary break-all">
            {formatEntropy(finalMinEntropy)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            bits per symbol (theoretical max: {theoreticalMax})
          </p>
        </div>

        {/* Entropy efficiency bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Entropy Efficiency</span>
            <span
              className={cn(
                'font-mono font-medium',
                efficiencyPct >= 90
                  ? 'text-green-600 dark:text-green-400'
                  : efficiencyPct >= 70
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-red-600 dark:text-red-400',
              )}
            >
              {efficiencyPct}%
            </span>
          </div>
          <Progress value={efficiencyPct} />
        </div>

        <Separator />

        {/* Individual estimators */}
        <div className="space-y-2 overflow-x-auto">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Individual Estimators
          </p>
          {estimates.map((est, idx) => {
            const isMin =
              Math.abs(est.minEntropy - finalMinEntropy) < 1e-9
            return (
              <div
                key={idx}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm',
                  isMin && 'bg-primary/5 ring-1 ring-primary/20',
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className={cn(
                      'h-2 w-2 rounded-full shrink-0',
                      isMin ? 'bg-primary' : 'bg-muted-foreground/30',
                    )}
                  />
                  <span className="truncate text-xs sm:text-sm">{est.estimatorName}</span>
                  {isMin && (
                    <Badge variant="default" className="text-[10px]">
                      MIN
                    </Badge>
                  )}
                </div>
                <span className="font-mono text-xs sm:text-sm shrink-0">
                  {formatEntropy(est.minEntropy)}
                </span>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
